import { isServer } from '@tanstack/react-query';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios, { AxiosError } from 'axios';
import NodeFormData from 'form-data';

import { isTruthy } from '@/utils/filter';
import type { RequestPathOptions, UrlParamsOptions } from '@/utils/url';
import { createUrlPathWithParams, formatUrl } from '@/utils/url';

export type FormDataEntry = string | number | boolean | Blob | File | Buffer;

type FastAPIValidationErrorEntry = {
  msg?: unknown;
  loc?: unknown;
};

/**
 * Format a FastAPI/Pydantic 422 `detail` array into a human-readable message.
 *
 * The backend's validation handler responds with
 * `{ detail: [{ loc, msg, ... }], message: 'Validation error occurred' }` — the real
 * explanation lives in each entry's `msg`, so surfacing the top-level `message` (or
 * stringifying the array) hides it. Each entry becomes `<loc path>: <msg>` with the
 * leading `body` segment dropped and Pydantic's `Value error, ` prefix stripped;
 * entries are joined with newlines.
 *
 * @param detail - The `detail` field of an error response body.
 * @returns The formatted message, or `null` when `detail` is not a non-empty array
 *   with readable entries.
 *
 * @example
 * formatValidationDetail([{ loc: ['body', 'inputs'], msg: 'Value error, Invalid expression' }])
 * // => 'inputs: Invalid expression'
 */
export function formatValidationDetail(detail: unknown): string | null {
  if (!Array.isArray(detail) || detail.length === 0) return null;
  const lines = detail
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (typeof entry !== 'object' || entry === null) return null;
      const { msg, loc } = entry as FastAPIValidationErrorEntry;
      if (typeof msg !== 'string' || msg.length === 0) return null;
      const message = msg.replace(/^Value error, /, '');
      const path = (Array.isArray(loc) ? loc : []).filter((part, index) => !(index === 0 && part === 'body')).join('.');
      return path ? `${path}: ${message}` : message;
    })
    .filter(isTruthy);
  return lines.length > 0 ? lines.join('\n') : null;
}

export class HTTPError extends Error {
  statuslessMessage: string;

  constructor(public response: AxiosResponse) {
    let message = response.statusText;

    if (typeof response.data === 'string' && response.data.length > 0) {
      message = response.data;
    }

    const validationMessage = formatValidationDetail(response.data?.detail);
    if (validationMessage) {
      message = validationMessage;
    } else if ((response.data?.message?.length ?? 0) > 0) {
      message = response.data.message;
    } else if ((response.data?.detail?.length ?? 0) > 0) {
      message = response.data.detail;
    } else if ((response.data?.error_description?.length ?? 0) > 0) {
      message = response.data.error_description;
    } else if ((response.data?.error?.message?.length ?? 0) > 0) {
      message = response.data.error.message;
    } else if ((response.data?.error?.detail?.length ?? 0) > 0) {
      message = response.data.error.detail;
    } else if ((response.data?.error?.error_description?.length ?? 0) > 0) {
      message = response.data.error.error_description;
    } else if ((response.data?.error?.error?.message?.length ?? 0) > 0) {
      message = response.data.error.error.message;
    } else if ((response.data?.error?.error?.detail?.length ?? 0) > 0) {
      message = response.data.error.error.detail;
    } else if ((response.data?.error?.error?.error_description?.length ?? 0) > 0) {
      message = response.data.error.error.error_description;
    }

    super(isServer ? message : `Error (${response.status}): ${message}`);
    this.name = 'HTTPError';
    this.statuslessMessage = message;
  }

  get rawMessage() {
    return this.statuslessMessage;
  }

  get status() {
    return this.response.status;
  }

  get data() {
    return this.response.data;
  }

  body() {
    try {
      return this.response.data;
    } catch (error) {
      console.error('[API] Error body:', error);
      return;
    }
  }
}

type CompatibleFormData = FormData | NodeFormData;

export type HTTPClientOptions = {
  baseUrl?: string;
  onUnauthorized?: () => Promise<boolean>;
  onRequestHeaders?: () => AxiosRequestConfig['headers'];
};

export type ExtendedRequestConfig = {
  rawResponse?: boolean;
  /**
   * @deprecated Use `path` parameter instead.
   *
   * @example
   * ```ts
   * const response = await client.get('/conversations/list', {
   *   urlParams: { query: { conversation_type } },
   * });
   * ```
   *
   * is equivalent to:
   * ```ts
   * const response = await client.get({
   *   path: '/conversations/list',
   *   search: { conversation_type },
   * });
   * ```
   */
  urlParams?: UrlParamsOptions;
};

export type RequestOptions<D = unknown> = ExtendedRequestConfig & AxiosRequestConfig<D>;

export type RequestReturnType<T, D, R extends boolean> = R extends true ? AxiosResponse<T, D> : T;

export class HTTPClient {
  private axiosInstance: AxiosInstance;
  private onUnauthorized?: () => Promise<boolean>;
  private onRequestHeaders?: () => AxiosRequestConfig['headers'];

  constructor({ baseUrl, onUnauthorized, onRequestHeaders }: HTTPClientOptions) {
    this.onUnauthorized = onUnauthorized;
    this.onRequestHeaders = onRequestHeaders;

    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      withCredentials: true,
      paramsSerializer: { indexes: null },
    });
  }

  private formatRequestConfig(path: string, config: AxiosRequestConfig): AxiosRequestConfig {
    const { method = 'GET', data, headers, ...rest } = config;

    const baseConfig: AxiosRequestConfig = {
      url: path,
      method,
      data,
      headers: { ...headers, ...this.onRequestHeaders?.() },
      ...rest,
    };

    const isBuffer =
      typeof Buffer !== 'undefined' && typeof (Buffer as any)?.isBuffer === 'function' && Buffer.isBuffer(data);

    if (!data || isBuffer || (typeof window !== 'undefined' && data instanceof FormData)) {
      return baseConfig;
    }

    if (typeof window === 'undefined' && typeof (data as any)?.getHeaders === 'function') {
      return {
        ...baseConfig,
        headers: {
          ...baseConfig.headers,
          ...((data as any).getHeaders?.() ?? {}),
        },
      };
    }

    // Default to JSON
    return {
      ...baseConfig,
      headers: {
        'Content-Type': 'application/json',
        ...baseConfig.headers,
      },
    };
  }

  async request<T, D = unknown, R extends boolean = false>(
    path: string | RequestPathOptions,
    options: RequestOptions<D> & { rawResponse?: R } = {},
  ): Promise<RequestReturnType<T, D, R>> {
    const { rawResponse = false as R, urlParams, ...config } = options;

    const formattedPath = typeof path === 'string' ? createUrlPathWithParams(path, urlParams) : formatUrl(path);

    const formattedConfig = this.formatRequestConfig(formattedPath, config);

    try {
      const response = await this.axiosInstance.request<T, AxiosResponse<T, D>>(formattedConfig);
      return (rawResponse ? response : response.data) as RequestReturnType<T, D, R>;
    } catch (error: any) {
      if (!(error instanceof AxiosError)) {
        throw error;
      }

      if (error.response?.status === 401 && this.onUnauthorized) {
        const shouldRetry = await this.onUnauthorized();
        if (shouldRetry) {
          const retryResponse = await this.axiosInstance.request<T, AxiosResponse<T, D>>(formattedConfig);
          return (rawResponse ? retryResponse : retryResponse.data) as RequestReturnType<T, D, R>;
        }
      }

      if (error.response) {
        const httpError = new HTTPError(error.response);
        console.error(
          `[HTTP Client | ${formattedConfig.method?.toUpperCase()} ${formattedPath}] Error message:`,
          httpError.statuslessMessage,
        );

        throw httpError;
      }
      throw error;
    }
  }

  get<T, D = unknown, R extends boolean = false>(
    path: string | RequestPathOptions,
    options: RequestOptions<D> & { rawResponse?: R } = {},
  ): Promise<RequestReturnType<T, D, R>> {
    return this.request<T, D, R>(path, { ...options, method: 'GET' });
  }

  post<T, D = unknown, R extends boolean = false>(
    path: string | RequestPathOptions,
    data?: D,
    options: RequestOptions<D> & { rawResponse?: R } = {},
  ): Promise<RequestReturnType<T, D, R>> {
    return this.request<T, D, R>(path, { ...options, method: 'POST', data });
  }

  put<T, D = unknown, R extends boolean = false>(
    path: string | RequestPathOptions,
    data?: D,
    options: RequestOptions<D> & { rawResponse?: R } = {},
  ): Promise<RequestReturnType<T, D, R>> {
    return this.request<T, D, R>(path, { ...options, method: 'PUT', data });
  }

  patch<T, D = unknown, R extends boolean = false>(
    path: string | RequestPathOptions,
    data?: D,
    options: RequestOptions<D> & { rawResponse?: R } = {},
  ): Promise<RequestReturnType<T, D, R>> {
    return this.request<T, D, R>(path, { ...options, method: 'PATCH', data });
  }

  delete<T, D = unknown, R extends boolean = false>(
    path: string | RequestPathOptions,
    options: RequestOptions<D> & { rawResponse?: R } = {},
  ): Promise<RequestReturnType<T, D, R>> {
    return this.request<T, D, R>(path, { ...options, method: 'DELETE' });
  }
}

export function extractHTTPError(error: any, defaultMessage: string, raw = false): string {
  if (raw && error instanceof HTTPError) return error.statuslessMessage;
  if (error?.['message']) return error['message'] ?? defaultMessage;
  return defaultMessage;
}

function createCompatibleFormData(): CompatibleFormData {
  if (typeof window === 'undefined') {
    return new NodeFormData();
  }
  return new FormData();
}

function formatFormDataEntry(value: FormDataEntry): string | Blob {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value.toString();
  }

  if (typeof File !== 'undefined' && value instanceof File) return value;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value;

  if (typeof Buffer !== 'undefined' && value instanceof Buffer) {
    // Browser: convert to Blob. Server: `Blob` may not exist, so return the Buffer as-is.
    if (typeof Blob !== 'undefined') return new Blob([new Uint8Array(value)]);
    return value as unknown as Blob;
  }

  throw new Error('Unsupported FormData entry value');
}

export function createFormData(data: Record<string, FormDataEntry | FormDataEntry[]>): CompatibleFormData {
  const formData = createCompatibleFormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((v) => formData.append(key, formatFormDataEntry(v)));
    } else {
      formData.append(key, formatFormDataEntry(value));
    }
  });
  return formData;
}
